import { useEffect, useState } from "react";
import { Tabs, Card, Divider, Row, Switch, Button } from "antd";
import PageTitle from "components/PageTitle";
import PlatformLabel from "components/PlatformLabel";
import BackendSettingForm from "./settings/BackendSettingForm";
import StorefrontClassificationSettingForm from "./settings/StorefrontClassificationSettingForm";
import StorefrontNonClassificationSettingForm from "./settings/StorefrontNonClassificationSettingForm";
// styles
import 'assets/styles/shipment.scss';
// request
import { getPlatforms } from 'requests/platform';
import { getModules } from 'requests/module';
import { getShipmentSettings, updateShipmentSettings, getShipmentWarehouses } from 'requests/shipment';
import { getActivedServices } from 'requests/service';

const { TabPane } = Tabs;

const titles = [
    { path: 'shipments', title: 'Shipment Advanced Settings' },
];
const orderTypes = [
    { label: 'Prepaid', value: 'prepaid' },
    { label: 'COD', value: 'cod' },
    { label: 'CCOD', value: 'ccod' }
];
const modes = [
    { label: 'Air', value: 'air' },
    { label: 'Surface', value: 'surface' }
];
const cartTotalOptions = [
    { label: 'Subtotal only', value: 'subtotal' },
    { label: 'Subtotal, taxes and shipping fees', value: 'estimated_order' }
];

const categories = [
    { label: 'Consumer Goods', value: 'Consumer Goods' },
    { label: 'Toys and Baby Products', value: 'Toys and Baby Products' },
    { label: 'Handbags and Luggage', value: 'Handbags and Luggage' },
    { label: 'Computers and Accessories', value: 'Computers and Accessories' },
    { label: 'Food Articles', value: 'Food Articles' },
    { label: 'Home Kitchen and Pets', value: 'Home Kitchen and Pets' },
    { label: 'Shoes', value: 'Shoes' },
    { label: 'Clothing and Accessories', value: 'Clothing and Accessories' },
    { label: 'Gift Item', value: 'Gift Item' },
    { label: 'Books', value: 'Books' },
    { label: 'Movies Music and Video Games', value: 'Movies Music and Video Games' },
    { label: 'Mobiles and Tablets', value: 'Mobiles and Tablets' },
    { label: 'Cameras Audio and Video', value: 'Cameras Audio and Video' },
    { label: 'Sports Fitness and Outdoors', value: 'Sports Fitness and Outdoors' },
    { label: 'Beauty Health and Gourmet', value: 'Beauty Health and Gourmet' },
    { label: 'Jewellery Watches and Eyewear', value: 'Jewellery Watches and Eyewear' },
    { label: 'Car Motorbike and Industrial', value: 'Car Motorbike and Industrial' },
    { label: 'Other', value: 'Other' },
];

const autoBookingModes = [
    { label: 'As per default settings', value: 'default_settings' },
    { label: 'As per selection by the customer', value: 'customer_selection' },
    { label: 'Lowest cost', value: 'lowest_of_all'}
];

const failoverAutoBookingModes = [
    { label: 'As per default settings', value: 'default_settings' },
    { label: 'Lowest cost', value: 'lowest_of_all'}
];

const ShipmentAdvancedSettings = () => {
    const [availablePlatforms, setAvailablePlatforms] = useState([]);
    const [platform, setPlatform] = useState('');
    const [type, setType] = useState('storefront');
    const [orderType, setOrderType] = useState('prepaid');
    const [isDisableOptionClassification, setIsDisableOptionClassification] = useState(false);
    const [services, setServices] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [data, setData] = useState({});
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    useEffect(() => {
        const getShipmentServices = async () => {
            const moduleResponse = await getModules({ type: 2 }); // get shipment modules
            const moduleIds = moduleResponse.records.map(module => module.id);

            let services = [];
            for (let i = 0; i < moduleIds.length; i++) {
                const serviceResponse = await getActivedServices(moduleIds[i], { is_paginate: 0 });
                const tmp = serviceResponse.records.map(record => record.service);
                services = services.concat(tmp);
            }
            setServices(services);

            // get warehouses
            const warehouseResponse = await getShipmentWarehouses();
            setWarehouses(warehouseResponse.records);
        }

        const getData = async () => {
            const platformResponse = await getPlatforms();
            const response = await getShipmentSettings();
            setAvailablePlatforms(platformResponse.records);
            if (platformResponse.records.length) {
                setPlatform(platformResponse.records[0].id);
            }
            setData(response.platform);
        }

        getShipmentServices();
        getData();
    }, []);

    useEffect(() => {
        setIsDisableOptionClassification(!!data[platform]?.storefront?.disable_option_classification);
    }, [data]);

    const updateData = (namePath, value) => {
        const newData = { ...data };
        const paths = namePath.split('.');

        let subData = newData;
        const length = paths.length;
        for (let i = 0; i < length - 1; i++) {
            let elem = paths[i];
            if (!subData[elem]) subData[elem] = {};
            subData = subData[elem];
        }
        subData[paths[length - 1]] = value;

        setData(newData);
    }

    const onChangeClassifyOptions = (checked) => {
        setIsDisableOptionClassification(checked);
        updateData(`${platform}.storefront.disable_option_classification`, checked);
    }

    const onSubmit = async () => {
        try {
            setLoadingSubmit(true);
            const settings = {
                shipment: {
                    platform: data
                }
            };
            await updateShipmentSettings(settings);
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingSubmit(false);
        }
    }

    return (
        <div>
            <PageTitle titles={titles} />
            <div>Manage the channels and their settings from this page.</div>
            <Tabs defaultActiveKey={platform} onChange={setPlatform}>
                {
                    availablePlatforms.map(item => (
                        <TabPane tab={<PlatformLabel type={item.id} />} key={item.id}></TabPane>
                    ))
                }
            </Tabs>
            <Divider />
            <Tabs defaultActiveKey={type} onChange={setType}>
                <TabPane tab="Storefront settings" key='storefront'>
                    <Card>
                        <Row justify="space-between" align="top" className='mb-16'>
                            <div>
                                <label>Disable classification of shipping options</label>
                                <div><small>By default, this toggle is disabled. Shipment options will be classified by order mode and type.</small></div>
                            </div>
                            <Switch
                                checked={data[platform]?.storefront?.disable_option_classification}
                                onChange={(checked) => onChangeClassifyOptions(checked)}
                            />
                        </Row>
                        <Divider />
                        {isDisableOptionClassification ? (
                            <StorefrontNonClassificationSettingForm
                                platform={platform}
                                data={data[platform]?.storefront || {}}
                                onChange={updateData}
                            />
                        ) : (
                            <StorefrontClassificationSettingForm
                                orderTypes={orderTypes}
                                modes={modes}
                                cartTotalOptions={cartTotalOptions}
                                platform={platform}
                                data={data[platform]?.storefront || {}}
                                onChange={updateData}
                            />
                        )}
                    </Card>
                </TabPane>
                <TabPane tab="Backend settings" key='backend'>
                    <Card>
                        <BackendSettingForm
                            platform={platform}
                            data={data[platform]?.backend || {}}
                            categories={categories}
                            autoBookingModes={autoBookingModes}
                            failoverAutoBookingModes={failoverAutoBookingModes}
                            modes={modes}
                            orderTypes={orderTypes}
                            services={services}
                            warehouses={warehouses}
                            onChange={updateData}
                        />
                    </Card>
                </TabPane>
            </Tabs>

            <Row justify='end' className='mt-16'>
                <Button type="primary" size="large" loading={loadingSubmit} onClick={onSubmit}>Save</Button>
            </Row>
        </div>
    )
}

export default ShipmentAdvancedSettings; 